import React, { useEffect, useRef } from 'react';
import type { Course, Athlete, SpiritedSegment } from '../types';

// Use a global 'L' since it's loaded from CDN
declare const L: any;

import { getCartoTileUrl, computePathDirectionArrows, createDirectionArrowMarker } from '../utils/mapUtils';

interface AthletePosition {
  id: string;
  name: string;
  color: string;
  position: { lat: number; lon: number };
}

interface RaceMapProps {
  course: Course;
  athletes: Athlete[];
  athletePositions: AthletePosition[];
  highlightedAthleteId: string | null;
  spotlightedAthleteId: string | null;
  mapView: 'full' | 'follow';
  leadAthletePosition: { lat: number; lon: number } | null;
  currentUserId?: string;
  sectorPath?: [number, number][]; // Group ride Race Sector, drawn in cyan over the course
  spiritedSegment?: SpiritedSegment; // Group ride Race Sector definition
}

const RaceMap: React.FC<RaceMapProps> = ({ course, athletes, athletePositions, highlightedAthleteId, spotlightedAthleteId, mapView, leadAthletePosition, currentUserId, sectorPath, spiritedSegment }) => {
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<any>(null);
  const coursePolylineRef = useRef<any>(null);
  const courseArrowsGroupRef = useRef<any>(null);
  const sectorPolylineRef = useRef<any>(null);
  const sectorArrowsGroupRef = useRef<any>(null);
  const sectorStartMarkerRef = useRef<any>(null);
  const sectorFinishMarkerRef = useRef<any>(null);
  const startMarkerRef = useRef<any>(null);
  const finishMarkerRef = useRef<any>(null);
  const athleteMarkersRef = useRef<Map<string, any>>(new Map());
  const matchMarkersLayerRef = useRef<any>(null);

  // Initialize map
  useEffect(() => {
    let map: any;
    if (mapContainerRef.current && !mapRef.current) {
      map = L.map(mapContainerRef.current, {
          zoomControl: false,
      }).setView(course.start, 15);

      L.tileLayer(getCartoTileUrl('dark_all'), {
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>',
        subdomains: 'abcd',
        maxZoom: 20
      }).addTo(map);
      
      L.control.zoom({ position: 'bottomright' }).addTo(map);

      mapRef.current = map;
    }
    
    // CRITICAL FIX: Cleanup function to remove map instance on component unmount
    return () => {
        if (mapRef.current) {
            mapRef.current.remove();
            mapRef.current = null;
        }
        athleteMarkersRef.current.clear();
        coursePolylineRef.current = null;
        courseArrowsGroupRef.current = null;
        sectorPolylineRef.current = null;
        sectorArrowsGroupRef.current = null;
        sectorStartMarkerRef.current = null;
        sectorFinishMarkerRef.current = null;
        startMarkerRef.current = null;
        finishMarkerRef.current = null;
        matchMarkersLayerRef.current = null;
    };
  }, []);

  // Draw course and markers
  useEffect(() => {
    if (!mapRef.current) return;

    // Manage course path polyline
    if (courseArrowsGroupRef.current) {
      courseArrowsGroupRef.current.clearLayers();
    } else {
      courseArrowsGroupRef.current = L.layerGroup().addTo(mapRef.current);
    }

    if (course.path.length > 0) {
      if (coursePolylineRef.current) {
        coursePolylineRef.current.remove();
      }
      const polyline = L.polyline(course.path, { color: '#4f46e5', weight: 4, opacity: 0.7 }).addTo(mapRef.current);
      coursePolylineRef.current = polyline;

      // Add direction arrows along the course path
      const arrows = computePathDirectionArrows(course.path);
      arrows.forEach(arrow => {
        const marker = createDirectionArrowMarker(L, arrow, {
          color: '#ffffff',
          outlineColor: '#1e1b4b',
          size: 14
        });
        marker.addTo(courseArrowsGroupRef.current);
      });
    } else if (coursePolylineRef.current) {
        // Remove polyline if path is cleared
        coursePolylineRef.current.remove();
        coursePolylineRef.current = null;
    }

    // Manage Start marker
    if (startMarkerRef.current) {
        startMarkerRef.current.remove();
        startMarkerRef.current = null;
    }
    if (course.start && (course.start[0] !== 0 || course.start[1] !== 0)) {
        const startIcon = L.divIcon({ className: 'text-2xl', html: '🏁' });
        startMarkerRef.current = L.marker(course.start, { icon: startIcon }).addTo(mapRef.current).bindTooltip("Start");
    }
    
    // Manage Finish marker
    if (finishMarkerRef.current) {
        finishMarkerRef.current.remove();
        finishMarkerRef.current = null;
    }
    if (course.finish && (course.finish[0] !== 0 || course.finish[1] !== 0)) {
        const finishIcon = L.divIcon({ className: 'text-2xl', html: '🏆' });
        finishMarkerRef.current = L.marker(course.finish, { icon: finishIcon }).addTo(mapRef.current).bindTooltip("Finish");
    }
  }, [course]);

  // Draw Race Sector (group rides) on top of the course
  useEffect(() => {
    if (!mapRef.current) return;
    if (sectorPolylineRef.current) {
      sectorPolylineRef.current.remove();
      sectorPolylineRef.current = null;
    }
    if (sectorArrowsGroupRef.current) {
      sectorArrowsGroupRef.current.clearLayers();
    } else {
      sectorArrowsGroupRef.current = L.layerGroup().addTo(mapRef.current);
    }
    if (sectorStartMarkerRef.current) {
      sectorStartMarkerRef.current.remove();
      sectorStartMarkerRef.current = null;
    }
    if (sectorFinishMarkerRef.current) {
      sectorFinishMarkerRef.current.remove();
      sectorFinishMarkerRef.current = null;
    }

    if (sectorPath && sectorPath.length > 1) {
      sectorPolylineRef.current = L.polyline(sectorPath, {
        color: '#06b6d4', // cyan-500
        weight: 6,
        opacity: 0.95,
        lineCap: 'round',
        lineJoin: 'round',
        interactive: false,
      }).addTo(mapRef.current);

      // Add direction arrows along race sector
      const sectorArrows = computePathDirectionArrows(sectorPath, 10);
      sectorArrows.forEach(arrow => {
        const marker = createDirectionArrowMarker(L, arrow, {
          color: '#ffffff',
          outlineColor: '#083344',
          size: 14
        });
        marker.addTo(sectorArrowsGroupRef.current);
      });

      const startPt: [number, number] = (spiritedSegment?.startLat && spiritedSegment?.startLon)
        ? [spiritedSegment.startLat, spiritedSegment.startLon]
        : sectorPath[0];
      const finishPt: [number, number] = (spiritedSegment?.finishLat && spiritedSegment?.finishLon)
        ? [spiritedSegment.finishLat, spiritedSegment.finishLon]
        : sectorPath[sectorPath.length - 1];

      // Add Sector Start Marker (⚡)
      const spiritedStartIcon = L.divIcon({
        className: 'custom-spirited-marker',
        html: `<div style="background-color: #06b6d4; width: 30px; height: 30px; border-radius: 50%; display: flex; align-items: center; justify-content: center; border: 2px solid white; box-shadow: 0 0 12px #06b6d4; font-size: 16px;">⚡</div>`,
        iconSize: [30, 30],
        iconAnchor: [15, 15]
      });
      sectorStartMarkerRef.current = L.marker(startPt, { icon: spiritedStartIcon })
        .addTo(mapRef.current)
        .bindTooltip('<b>⚡ Sector Start</b><br/>Pace Accelerates Here', { permanent: false });

      // Add Sector End Marker (🏆)
      const spiritedFinishIcon = L.divIcon({
        className: 'custom-spirited-marker',
        html: `<div style="background-color: #06b6d4; width: 30px; height: 30px; border-radius: 50%; display: flex; align-items: center; justify-content: center; border: 2px solid white; box-shadow: 0 0 12px #06b6d4; font-size: 16px;">🏆</div>`,
        iconSize: [30, 30],
        iconAnchor: [15, 15]
      });
      sectorFinishMarkerRef.current = L.marker(finishPt, { icon: spiritedFinishIcon })
        .addTo(mapRef.current)
        .bindTooltip('<b>🏁 Sector End</b><br/>Sprint / Finish Line', { permanent: false });
    }
  }, [sectorPath, spiritedSegment, course]);

  // Manage map view: follow lead athlete
  useEffect(() => {
    if (mapView === 'follow' && mapRef.current && leadAthletePosition) {
        const targetLatLng = L.latLng(leadAthletePosition.lat, leadAthletePosition.lon);
        
        // Using flyTo for a smoother, more robust transition that often handles centering better.
        mapRef.current.flyTo(targetLatLng, 17, {
            animate: true,
            duration: 0.5, // duration in seconds
        });
    }
  }, [mapView, leadAthletePosition]);

  // Manage map view: fit to full course
  useEffect(() => {
    if (mapView === 'full' && mapRef.current && coursePolylineRef.current) {
        mapRef.current.fitBounds(coursePolylineRef.current.getBounds().pad(0.1));
    }
  }, [mapView, course.path]);

  // Update athlete markers
  useEffect(() => {
    if (!mapRef.current) return;
    const markers = athleteMarkersRef.current;
    
    // Prune markers for athletes no longer present
    const activeIds = new Set(athletePositions.map(a => a.id));
    for (const [id, marker] of markers.entries()) {
      if (!activeIds.has(id)) {
        try { marker.remove(); } catch (e) {}
        markers.delete(id);
      }
    }

    athletePositions.forEach(({ id, name, position, color }) => {
      if (typeof position?.lat !== 'number' || typeof position?.lon !== 'number' || isNaN(position.lat) || isNaN(position.lon)) {
        return;
      }

      const latLng = [position.lat, position.lon];
      const isHighlighted = id === highlightedAthleteId;
      const isSpotlighted = id === spotlightedAthleteId;

      let iconClasses = 'w-4 h-4 rounded-full border-2 transition-all duration-200 shadow-lg flex items-center justify-center font-bold text-xs text-white';

      if (isHighlighted) {
          iconClasses += ' border-white scale-150 ring-2 ring-white';
      } else if (isSpotlighted) {
          iconClasses += ' border-yellow-400 scale-125 ring-2 ring-yellow-400';
      } else {
          iconClasses += ' border-gray-900';
      }

      const iconHtml = `<div style="background-color: ${color || '#34d399'};" class="${iconClasses}"></div>`;

      const icon = L.divIcon({
        html: iconHtml,
        className: 'custom-div-icon', // This class is mainly for targeting if needed, styling is inline
        iconSize: [isHighlighted ? 24 : 16, isHighlighted ? 24 : 16], // Adjust size for scaling
        iconAnchor: [isHighlighted ? 12 : 8, isHighlighted ? 12 : 8],
      });

      let zIndexOffset = 0;
      if (isHighlighted) {
          zIndexOffset = 1000;
      } else if (isSpotlighted) {
          zIndexOffset = 500;
      }
      
      const existingMarker = markers.get(id);
      if (existingMarker && mapRef.current.hasLayer(existingMarker)) {
        existingMarker.setLatLng(latLng).setIcon(icon).setZIndexOffset(zIndexOffset);
      } else {
        if (existingMarker) {
          try { existingMarker.remove(); } catch (e) {}
        }
        const marker = L.marker(latLng, { icon, zIndexOffset }).addTo(mapRef.current).bindTooltip(name);
        markers.set(id, marker);
      }
    });

  }, [athletePositions, highlightedAthleteId, spotlightedAthleteId]);

  // Initialize match markers layer
  useEffect(() => {
     if (!mapRef.current) return;
     if (!matchMarkersLayerRef.current) {
         matchMarkersLayerRef.current = L.layerGroup().addTo(mapRef.current);
     }
     
     const layer = matchMarkersLayerRef.current;
     layer.clearLayers();

     athletes.forEach(athlete => {
         // Security/Privacy: Never show matches for other athletes
         const isCurrentUser = currentUserId && (athlete.userId === currentUserId || athlete.id === currentUserId);
         if (!isCurrentUser) return;

         // If the user has explicitly selected/spotlighted a different athlete, 
         // we don't show the logged-in user's matches to reduce map clutter.
         const hasSelection = highlightedAthleteId || spotlightedAthleteId;
         const isSelected = athlete.id === highlightedAthleteId || athlete.id === spotlightedAthleteId;
         if (hasSelection && !isSelected) return;

         if (!athlete.matchEvents || athlete.matchEvents.length === 0) return;

         athlete.matchEvents.forEach(match => {
             const iconHtml = `<div style="font-size: 20px; text-shadow: 0 2px 4px rgba(0,0,0,0.5); transform: translateY(-50%);">🔥</div>`;
             const icon = L.divIcon({ className: 'bg-transparent border-0', html: iconHtml, iconSize: [24, 24], iconAnchor: [12, 12] });

             const activeDuration = match.activeBurnDuration ? Math.round(match.activeBurnDuration) : Math.round(match.durationSeconds);
             const activePower = match.activeBurnAvgPower ? Math.round(match.activeBurnAvgPower) : Math.round(match.avgPower);

             const formattedActiveDuration = activeDuration >= 60 
                 ? `${Math.floor(activeDuration / 60)}:${(activeDuration % 60).toString().padStart(2, '0')}`
                 : `${activeDuration}s`;

             L.marker([match.lat, match.lon], { icon, zIndexOffset: 2000 })
                 .bindTooltip(`<b>${athlete.name}</b><br/>Burned a Match<br/>${activePower}W for ${formattedActiveDuration}`)
                 .addTo(layer);
         });
     });
  }, [athletes, highlightedAthleteId, spotlightedAthleteId, currentUserId]);

  return <div ref={mapContainerRef} className="w-full h-full bg-gray-800" />;
};

export default RaceMap;